import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { Search } from '@factorialco/f0-react/icons/app'
import moment from 'moment'

import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'

import FormBlock from 'modules/expenses/components/expenses/New/PerDiemWizard/FormBlock'

interface AppliedConcept {
  type: string
  conceptTotalCents: number
  deductionsApplied: Array<{
    amountCents?: number | null
    percentage?: number | null
    type?: string | null
  }>
}

interface PerDiemDay {
  date: string
  dayTotalCents: number
  appliedConcepts: AppliedConcept[]
}

interface DailyBreakdownProps {
  days: Array<PerDiemDay | null> | null
  totalAmountCents: number
  currency: string
  locale: string
  isLoading?: boolean
}

const DailyBreakdown = ({
  days,
  totalAmountCents,
  currency,
  locale,
  isLoading = false,
}: DailyBreakdownProps) => {
  return (
    <FormBlock
      divider
      collapsedTitle={i18n.t(
        'expenses_v2.forms.germany_per_diem_modal.breakdown.see_full_breakdown'
      )}
      expandedTitle={i18n.t(
        'expenses_v2.forms.germany_per_diem_modal.breakdown.hide_full_breakdown'
      )}
      collapsible
      defaultCollapsed
      titleIcon={Search}
    >
      <Box flexDirection="row" justifyContent="spaceBetween">
        <Text.Normal size="300" weight="normal" color="grey1000">
          {i18n.t('expenses_v2.forms.germany_per_diem_modal.breakdown.reimbursement_total')}
        </Text.Normal>
        <Text.Normal size="300" weight="semibold" color="grey1000">
          {isCurrency(currency)
            ? MoneyWithCurrency({
                amount: totalAmountCents,
                currency: currency,
                locale: locale,
              })
            : `${totalAmountCents / 100} ${currency}`}
        </Text.Normal>
      </Box>

      {isLoading && (
        <Box padding="s16" justifyContent="center">
          <Text.Normal size="200" color="grey600">
            Loading breakdown...
          </Text.Normal>
        </Box>
      )}

      {days && days.length > 0 && (
        <Box flexDirection="column" gap="s24" style={{ marginTop: 'var(--spacing-s8)' }}>
          {days
            .filter((day): day is NonNullable<typeof day> => day !== null && day !== undefined)
            .map((day) => {
              const baseRateConcept = day.appliedConcepts.find((c) => c.type === 'full_day')
              const overnightConcept = day.appliedConcepts.find((c) => c.type === 'overnight')
              const baseRateCents = baseRateConcept?.conceptTotalCents ?? 0
              const overnightRateCents = overnightConcept?.conceptTotalCents ?? 0

              // Calculate total deductions from all applied concepts
              const totalDeductionsCents = day.appliedConcepts.reduce((total, concept) => {
                const conceptDeductions = concept.deductionsApplied.reduce(
                  (sum, deduction) => sum + (deduction.amountCents ?? 0),
                  0
                )
                return total + conceptDeductions
              }, 0)

              return (
                <Box key={day.date} flexDirection="column" gap="s2">
                  <Text.Normal size="200" weight="medium" color="grey1000">
                    {i18nDateToString(moment(day.date), 'ui_long_with_year')}
                  </Text.Normal>
                  <Box paddingLeft="s4" flexDirection="column" gap="s1">
                    {baseRateCents > 0 && (
                      <Box flexDirection="row" justifyContent="spaceBetween">
                        <Text.Normal size="200" weight="normal" color="grey700">
                          Base rate:
                        </Text.Normal>
                        <Text.Normal size="200" weight="medium" color="grey1000">
                          {isCurrency(currency)
                            ? MoneyWithCurrency({
                                amount: baseRateCents,
                                currency: currency,
                                locale: locale,
                              })
                            : '0'}
                        </Text.Normal>
                      </Box>
                    )}
                    {overnightRateCents > 0 && (
                      <Box flexDirection="row" justifyContent="spaceBetween">
                        <Text.Normal size="200" weight="normal" color="grey700">
                          Overnight rate:
                        </Text.Normal>
                        <Text.Normal size="200" weight="medium" color="grey1000">
                          {isCurrency(currency)
                            ? MoneyWithCurrency({
                                amount: overnightRateCents,
                                currency: currency,
                                locale: locale,
                              })
                            : '0'}
                        </Text.Normal>
                      </Box>
                    )}
                    {totalDeductionsCents > 0 && (
                      <Box flexDirection="row" justifyContent="spaceBetween">
                        <Text.Normal size="200" weight="normal" color="grey700">
                          Total deductions:
                        </Text.Normal>
                        <Text.Normal size="200" weight="medium" color="grey1000">
                          {isCurrency(currency)
                            ? MoneyWithCurrency({
                                amount: totalDeductionsCents,
                                currency: currency,
                                locale: locale,
                              })
                            : '0'}
                        </Text.Normal>
                      </Box>
                    )}
                    <Box
                      flexDirection="row"
                      justifyContent="spaceBetween"
                      style={{ marginTop: 'var(--spacing-s2)' }}
                    >
                      <Text.Normal size="200" weight="medium" color="grey1000">
                        Day total:
                      </Text.Normal>
                      <Text.Normal size="200" weight="semibold" color="grey1000">
                        {isCurrency(currency)
                          ? MoneyWithCurrency({
                              amount: day.dayTotalCents,
                              currency: currency,
                              locale: locale,
                            })
                          : '0'}
                      </Text.Normal>
                    </Box>
                  </Box>
                </Box>
              )
            })}
        </Box>
      )}
    </FormBlock>
  )
}

export default DailyBreakdown
